import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  InputNumber,
  Input,
  Button,
  Divider,
  message,
} from 'antd';
import { X, Package } from 'lucide-react';
import { useGetClients } from '~/services/client/client.queries';
import { useGetLocationsInfinite } from '~/services/common';
import { useCreateInventory } from '~/services/inventory';
import { PackingTypes, InventoryType, ContainerType, InventoryStatusCode, ClientStatus } from '~/utils/constants';
import { InfiniteScrollSelect } from '~/components/common';

interface QuickPkgFormData {
  clientId: string;
  quantity: number;
  containerType: ContainerType;
  description: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  status: InventoryStatusCode;
  locationId?: string;
}

interface QuickPkgFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormRow {
  id: string;
  data: QuickPkgFormData;
  isCompleted: boolean;
}

const containerTypeOptions = [
  { label: "Box", value: ContainerType.BOX },
  { label: "Crate", value: ContainerType.CRATE },
  { label: "Slat Crate", value: ContainerType.SLATCRATE },
  { label: "T-Frame", value: ContainerType.T_Frame },
  { label: "Pallet", value: ContainerType.PALLET },
  { label: "C-Bin", value: ContainerType.CBIN },
  { label: "Slipcase", value: ContainerType.SLIPCASE },
  { label: "Custom", value: ContainerType.CUSTOM },
];

const getStatusColor = (status: InventoryStatusCode) => {
  switch (status) {
    case InventoryStatusCode.AVAILABLE_LIVE:
      return "#52c41a";
    case InventoryStatusCode.AVAILABLE_ON_HAND:
      return "#1890ff";
    case InventoryStatusCode.NO_POSSESSION:
      return "#fa8c16";
    case InventoryStatusCode.RELEASED:
      return "#722ed1";
    default:
      return "#d9d9d9";
  }
};

const statusOptions = [
  {
    label: (
      <div className="flex items-center space-x-2">
        <div
          className="w-3 h-3 rounded-full"
          style={{
            backgroundColor: getStatusColor(InventoryStatusCode.NO_POSSESSION),
          }}
        />
        <span>Not Collected</span>
      </div>
    ),
    value: InventoryStatusCode.NO_POSSESSION,
  },
  {
    label: (
      <div className="flex items-center space-x-2">
        <div
          className="w-3 h-3 rounded-full"
          style={{
            backgroundColor: getStatusColor(InventoryStatusCode.AVAILABLE_LIVE),
          }}
        />
        <span>Available - Live</span>
      </div>
    ),
    value: InventoryStatusCode.AVAILABLE_LIVE,
  },
  {
    label: (
      <div className="flex items-center space-x-2">
        <div
          className="w-3 h-3 rounded-full"
          style={{
            backgroundColor: getStatusColor(InventoryStatusCode.AVAILABLE_ON_HAND),
          }}
        />
        <span>Available - On Hand</span>
      </div>
    ),
    value: InventoryStatusCode.AVAILABLE_ON_HAND,
  },
  {
    label: (
      <div className="flex items-center space-x-2">
        <div
          className="w-3 h-3 rounded-full"
          style={{
            backgroundColor: getStatusColor(InventoryStatusCode.RELEASED),
          }}
        />
        <span>Released</span>
      </div>
    ),
    value: InventoryStatusCode.RELEASED,
  },
];

const QuickPkgForm: React.FC<QuickPkgFormProps> = ({ open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [rows, setRows] = useState<FormRow[]>([
    {
      id: '1',
      data: {
        clientId: '',
        quantity: 1,
        containerType: ContainerType.BOX,
        description: '',
        length: 0,
        width: 0,
        height: 0,
        weight: 15,
        status: InventoryStatusCode.NO_POSSESSION,
        locationId: undefined,
      },
      isCompleted: false,
    },
  ]);
  const [currentRowId, setCurrentRowId] = useState('1');
  const [selectedClient, setSelectedClient] = useState<string>('');

  // Match the exact structure from the working inventory form
  const { data: clients, isLoading: clientsLoading } = useGetClients({
    params: { status: ClientStatus.ACTIVE, sortByName: "ASC", getAll: true },
  });

  // Debug logging
  console.log('Quick Pkg - clients data:', clients);
  console.log('Quick Pkg - total clients:', clients?.meta?.totalItems);

  const {
    data: locations,
    isLoading: locationsLoading,
    fetchNextPage: fetchNextLocationPage,
    hasNextPage: hasNextLocationPage,
  } = useGetLocationsInfinite({
    page: 1,
    limit: 50,
  });

  const createInventoryMutation = useCreateInventory();

  useEffect(() => {
    if (open) {
      // Reset form when modal opens
      setRows([
        {
          id: '1',
          data: {
            clientId: '',
            quantity: 1,
            containerType: ContainerType.BOX,
            description: '',
            length: 0,
            width: 0,
            height: 0,
            weight: 15,
            status: InventoryStatusCode.NO_POSSESSION,
            locationId: undefined,
          },
          isCompleted: false,
        },
      ]);
      setCurrentRowId('1');
      setSelectedClient('');
      form.resetFields();
    }
  }, [open, form]);

  const getCurrentRow = () => rows.find(row => row.id === currentRowId);

  const updateCurrentRow = (data: Partial<QuickPkgFormData>) => {
    setRows(prev => prev.map(row => 
      row.id === currentRowId 
        ? { ...row, data: { ...row.data, ...data } }
        : row
    ));
  };

  const createInventoryItems = async (rowData: QuickPkgFormData) => {
    console.log(`Creating ${rowData.quantity} inventory items sequentially...`);
    const results = [];
    
    // Create items sequentially to avoid duplicate inventory reference conflicts
    for (let i = 0; i < rowData.quantity; i++) {
      console.log(`Creating item ${i + 1} of ${rowData.quantity}`);
      
      // Complete package payload
      const inventoryData = {
        title: rowData.description,
        clientId: rowData.clientId,
        length: rowData.length,
        width: rowData.width,
        height: rowData.height,
        weight: rowData.weight,
        status: rowData.status,
        // Essential package fields
        itemType: InventoryType.PACKAGE,
        containerType: rowData.containerType,
        // Optional fields with defaults
        artistName: null,
        clientInternalRef: null,
        duplicates: 0,
        packingType: null, // For packages
        medium: null,
        value: null,
        parts: null,
        notes: null,
        description: rowData.description, // Same as title
        specialHandling: [],
      };

      // Only add locationId if it exists
      if (rowData.locationId) {
        inventoryData.locationId = rowData.locationId;
      }

      console.log(`Package payload for item ${i + 1}:`, inventoryData);

      try {
        // Create items one by one, waiting for each to complete
        const result = await createInventoryMutation.mutateAsync(inventoryData);
        results.push(result);
        console.log(`Package ${i + 1} created successfully:`, result);
      } catch (error) {
        console.error(`Failed to create package ${i + 1}:`, error);
        throw new Error(`Failed to create package ${i + 1}: ${error.message}`);
      }
    }

    console.log('All packages created successfully:', results);
    message.success(`Successfully created ${rowData.quantity} package(s)!`);
    return results;
  };

  const handleSave = async () => {
    try {
      const currentRow = getCurrentRow();
      if (!currentRow) return;

      // Validate current row
      if (!currentRow.data.clientId || !currentRow.data.description || 
          !currentRow.data.length || !currentRow.data.width || !currentRow.data.height) {
        message.error('Please fill in all required fields');
        return;
      }

      // Check if location is required but missing
      if (currentRow.data.status === InventoryStatusCode.AVAILABLE_ON_HAND && !currentRow.data.locationId) {
        message.error('Location is required when status is "Available - On Hand"');
        return;
      }

      await createInventoryItems(currentRow.data);
      
      // Call success callback and close modal
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error creating inventory:', error);
      // Error message is already shown in createInventoryItems
    }
  };

  const handleSaveAndNew = async () => {
    try {
      const currentRow = getCurrentRow();
      if (!currentRow) return;

      // Validate current row
      if (!currentRow.data.clientId || !currentRow.data.description || 
          !currentRow.data.length || !currentRow.data.width || !currentRow.data.height) {
        message.error('Please fill in all required fields');
        return;
      }

      // Check if location is required but missing
      if (currentRow.data.status === InventoryStatusCode.AVAILABLE_ON_HAND && !currentRow.data.locationId) {
        message.error('Location is required when status is "Available - On Hand"');
        return;
      }

      await createInventoryItems(currentRow.data);

      // Mark current row as completed (for display purposes)
      setRows(prev => prev.map(row => 
        row.id === currentRowId 
          ? { ...row, isCompleted: true }
          : row
      ));

      // Add new row
      const newRowId = String(Date.now());
      const newRow: FormRow = {
        id: newRowId,
        data: {
          clientId: selectedClient,
          quantity: 1,
          containerType: ContainerType.BOX,
          description: '',
          length: 0,
          width: 0,
          height: 0,
          weight: 15,
          status: InventoryStatusCode.NO_POSSESSION,
          locationId: undefined,
        },
        isCompleted: false,
      };

      setRows(prev => [...prev, newRow]);
      setCurrentRowId(newRowId);

      // Reset form for new row but keep client
      form.setFieldsValue({
        clientId: selectedClient,
        quantity: 1,
        containerType: ContainerType.BOX,
        description: '',
        length: 0,
        width: 0,
        height: 0,
        weight: 15,
        status: InventoryStatusCode.NO_POSSESSION,
        locationId: undefined,
      });

      // Additional success message for Save and New
      message.success(`Created ${currentRow.data.quantity} items! Ready for next entry.`);
    } catch (error) {
      console.error('Error creating inventory:', error);
      // Error message is already shown in createInventoryItems
    }
  };

  const handleFieldChange = (field: keyof QuickPkgFormData, value: any) => {
    updateCurrentRow({ [field]: value });
    
    // Update the form field using Ant Design's form methods
    form.setFieldValue(field, value);
    
    if (field === 'clientId') {
      setSelectedClient(value);
    }

    // Clear location when status is not AVAILABLE_ON_HAND
    if (field === 'status' && value !== InventoryStatusCode.AVAILABLE_ON_HAND) {
      updateCurrentRow({ [field]: value, locationId: undefined });
      form.setFieldValue('locationId', undefined);
    }
  };

  const currentRow = getCurrentRow();
  const showLocationField = currentRow?.data.status === InventoryStatusCode.AVAILABLE_ON_HAND;

  // Flatten locations for the dropdown
  const flattenedLocations = locations?.pages.flatMap(page => page.items) || [];

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <Package size={20} className="text-blue-600" />
          <span>Quick Package Entry</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={1000}
      footer={null}
      closeIcon={<X size={16} />}
    >
      <div className="space-y-4">
        {/* Show completed rows */}
        {rows.filter(row => row.isCompleted).map((row, index) => (
          <div key={row.id} className="bg-gray-50 p-4 rounded border">
            <div className="text-sm text-gray-600 mb-2">
              Completed Entry #{index + 1}
            </div>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium">Client:</span> {
                  clients?.items.find(c => c.id === row.data.clientId)?.companyName ||
                  clients?.items.find(c => c.id === row.data.clientId)?.firstName + ' ' +
                  clients?.items.find(c => c.id === row.data.clientId)?.lastName ||
                  'Unknown'
                }
              </div>
              <div>
                <span className="font-medium">Quantity:</span> {row.data.quantity}
              </div>
              <div>
                <span className="font-medium">Description:</span> {row.data.description}
              </div>
              <div>
                <span className="font-medium">Dimensions:</span> {row.data.length}" × {row.data.width}" × {row.data.height}"
              </div>
            </div>
          </div>
        ))}

        {/* Current active form */}
        <Form
          form={form}
          layout="vertical"
          initialValues={currentRow?.data}
        >
          <div className="bg-white border rounded p-4">
            {/* Row 1: Client, Quantity, Container Type */}
            <div className="grid grid-cols-3 gap-6 mb-4">
              <Form.Item
                label="Client"
                name="clientId"
                rules={[{ required: true, message: 'Please select a client' }]}
              >
                <Select
                  placeholder="Select client"
                  showSearch
                  loading={clientsLoading}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={currentRow?.data.clientId}
                  onChange={(value) => handleFieldChange('clientId', value)}
                  options={clients?.items.map((client) => ({
                    value: client.id,
                    label:
                      client.companyName ||
                      [client.firstName, client.lastName]
                        .filter(Boolean)
                        .join(' ')
                        .trim() ||
                      'Unnamed Client',
                  }))}
                />
              </Form.Item>

              <Form.Item
                label="Quantity"
                name="quantity"
                rules={[{ required: true, message: 'Please enter quantity' }]}
              >
                <InputNumber
                  min={1}
                  max={100}
                  value={currentRow?.data.quantity}
                  onChange={(value) => handleFieldChange('quantity', value || 1)}
                  className="w-full"
                />
              </Form.Item>

              <Form.Item
                label="Container Type"
                name="containerType"
                rules={[{ required: true, message: 'Please select container type' }]}
              >
                <Select
                  placeholder="Select container type"
                  value={currentRow?.data.containerType}
                  onChange={(value) => handleFieldChange('containerType', value)}
                  options={containerTypeOptions}
                />
              </Form.Item>
            </div>

            {/* Row 2: Description, Length */}
            <div className="grid grid-cols-2 gap-6 mb-4">
              <Form.Item
                label="Description"
                name="description"
                rules={[{ required: true, message: 'Please enter description' }]}
              >
                <Input
                  placeholder="e.g., Small boxed paintings"
                  value={currentRow?.data.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                />
              </Form.Item>

              <Form.Item
                label="Length (inches)"
                name="length"
                rules={[{ required: true, message: 'Please enter length' }]}
              >
                <InputNumber
                  min={0}
                  step={1}
                  value={currentRow?.data.length}
                  onChange={(value) => handleFieldChange('length', value || 0)}
                  className="w-full"
                />
              </Form.Item>
            </div>

            {/* Row 3: Width, Height, Weight */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <Form.Item
                label="Width (inches)"
                name="width"
                rules={[{ required: true, message: 'Please enter width' }]}
              >
                <InputNumber
                  min={0}
                  step={1}
                  value={currentRow?.data.width}
                  onChange={(value) => handleFieldChange('width', value || 0)}
                  className="w-full"
                />
              </Form.Item>

              <Form.Item
                label="Height (inches)"
                name="height"
                rules={[{ required: true, message: 'Please enter height' }]}
              >
                <InputNumber
                  min={0}
                  step={1}
                  value={currentRow?.data.height}
                  onChange={(value) => handleFieldChange('height', value || 0)}
                  className="w-full"
                />
              </Form.Item>

              <Form.Item
                label="Weight (lbs)"
                name="weight"
                rules={[{ required: true, message: 'Please enter weight' }]}
              >
                <InputNumber
                  min={0}
                  step={1}
                  value={currentRow?.data.weight}
                  onChange={(value) => handleFieldChange('weight', value || 15)}
                  className="w-full"
                />
              </Form.Item>
            </div>

            {/* Row 4: Status, Location (conditional) */}
            <div className={`grid gap-6 mb-4 ${showLocationField ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select
                  placeholder="Select status"
                  value={currentRow?.data.status}
                  onChange={(value) => handleFieldChange('status', value)}
                  options={statusOptions}
                />
              </Form.Item>

              {showLocationField && (
                <Form.Item
                  label="Location"
                  name="locationId"
                  rules={[{ required: true, message: 'Please select location when status is "Available - On Hand"' }]}
                >
                  <InfiniteScrollSelect
                    placeholder="Select location"
                    loading={locationsLoading}
                    value={currentRow?.data.locationId}
                    onChange={(value) => handleFieldChange('locationId', value)}
                    options={flattenedLocations.map((location) => ({
                      label: location.name,
                      value: location.id,
                    }))}
                    onLoadMore={() => {
                      if (hasNextLocationPage) {
                        fetchNextLocationPage();
                      }
                    }}
                    hasMore={hasNextLocationPage}
                  />
                </Form.Item>
              )}
            </div>
          </div>
        </Form>

        <Divider />

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={createInventoryMutation.isPending}
          >
            Save
          </Button>
          <Button
            type="primary"
            onClick={handleSaveAndNew}
            loading={createInventoryMutation.isPending}
            className="bg-green-600 hover:bg-green-700 border-green-600"
          >
            Save and New
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default QuickPkgForm;